import React, { useCallback, useEffect, useState } from "react";
import Sidebar from "../../components/Sidebar";
import axiosInstance from "../../utils/api";
import toast from "react-hot-toast";
import {
  FiActivity,
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiRefreshCw,
  FiXCircle,
} from "react-icons/fi";

const LEVELS = [
  { key: "", label: "All" },
  { key: "success", label: "Success" },
  { key: "warning", label: "Warnings" },
  { key: "error", label: "Errors" },
];

const levelStyle = {
  success: "bg-emerald-50 text-emerald-800 border-emerald-200",
  warning: "bg-amber-50 text-amber-900 border-amber-200",
  error: "bg-red-50 text-red-800 border-red-200",
};

/** Remove IPv4/IPv6 from actor label for the table (IPs stay in expanded JSON if needed). */
const stripIpFromDisplay = (s) => {
  if (!s || typeof s !== "string") return "—";
  const ipv4 =
    /(\s*·\s*)?\b(?:(?:25[0-5]|2[0-4]\d|[01]?\d{1,2})\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d{1,2})\b/g;
  const ipv6 = /(\s*·\s*)?(([0-9a-f]{1,4}:){2,}[0-9a-f:.]+|::1)\b/gi;
  let out = s
    .replace(ipv4, "")
    .replace(ipv6, "")
    .replace(/\s*·\s*·/g, " · ")
    .replace(/^\s*·\s*/, "")
    .replace(/\s*·\s*$/g, "")
    .trim();
  return out || "—";
};

const Monitoring = () => {
  const [level, setLevel] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    logs: [],
    total: 0,
    pages: 1,
    summary: { success: 0, warning: 0, error: 0 },
  });
  const [expanded, setExpanded] = useState({});

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: String(page), limit: "50" });
      if (level) params.set("level", level);
      const res = await axiosInstance.get(`/monitoring/logs?${params.toString()}`);
      setData({
        logs: res.data.logs || [],
        total: res.data.total ?? 0,
        pages: res.data.pages ?? 1,
        summary: res.data.summary || { success: 0, warning: 0, error: 0 },
      });
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not load monitoring logs");
    } finally {
      setLoading(false);
    }
  }, [level, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const toggleRow = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const formatTime = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const metaKeys = (meta) => {
    if (!meta || typeof meta !== "object") return [];
    return Object.keys(meta);
  };

  const actorLine = (row) => {
    const m = row.meta || {};
    let raw = "—";
    if (m.actorSummary) raw = m.actorSummary;
    else if (m.actorName) {
      raw = m.actorRole ? `${m.actorName} (${m.actorRole})` : m.actorName;
    } else if (m.emailMasked) raw = m.emailMasked;
    else if (m.ip) raw = "Guest";
    return stripIpFromDisplay(raw);
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      <Sidebar />

      <main className="flex-1 p-6 overflow-y-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <FiActivity className="text-blue-900" />
              Monitoring
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Operational events from the API: WhatsApp sends, PDF downloads, scans, and auth checks.
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchLogs()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 flex items-center gap-3">
            <FiCheckCircle className="text-emerald-600 text-xl shrink-0" />
            <div>
              <p className="text-xs font-medium text-emerald-800 uppercase tracking-wide">Success</p>
              <p className="text-2xl font-bold text-emerald-900">{data.summary.success}</p>
            </div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 flex items-center gap-3">
            <FiAlertTriangle className="text-amber-600 text-xl shrink-0" />
            <div>
              <p className="text-xs font-medium text-amber-900 uppercase tracking-wide">Warnings</p>
              <p className="text-2xl font-bold text-amber-950">{data.summary.warning}</p>
            </div>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 flex items-center gap-3">
            <FiXCircle className="text-red-600 text-xl shrink-0" />
            <div>
              <p className="text-xs font-medium text-red-800 uppercase tracking-wide">Errors</p>
              <p className="text-2xl font-bold text-red-900">{data.summary.error}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {LEVELS.map((l) => (
            <button
              key={l.key || "all"}
              type="button"
              onClick={() => {
                setPage(1);
                setLevel(l.key);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                level === l.key
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {loading && data.logs.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">Loading…</div>
          ) : data.logs.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">No log entries yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-3 w-10" />
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">Level</th>
                    <th className="px-4 py-3">Actor / source</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.logs.map((row) => {
                    const id = row._id;
                    const hasMeta = metaKeys(row.meta).length > 0;
                    const open = expanded[id];
                    return (
                      <React.Fragment key={id}>
                        <tr className="hover:bg-slate-50/80">
                          <td className="px-4 py-3 align-top">
                            {hasMeta ? (
                              <button
                                type="button"
                                onClick={() => toggleRow(id)}
                                className="p-1 rounded text-slate-500 hover:bg-slate-200"
                                aria-label="Toggle details"
                              >
                                {open ? <FiChevronDown /> : <FiChevronRight />}
                              </button>
                            ) : (
                              <span className="inline-block w-6" />
                            )}
                          </td>
                          <td className="px-4 py-3 align-top text-slate-600 whitespace-nowrap">
                            {formatTime(row.createdAt)}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${levelStyle[row.level] || "bg-slate-100 text-slate-700"}`}
                            >
                              {row.level}
                            </span>
                          </td>
                          <td className="px-4 py-3 align-top text-slate-700 text-xs max-w-[220px] leading-snug">
                            {actorLine(row)}
                          </td>
                          <td className="px-4 py-3 align-top font-mono text-xs text-slate-700">
                            {row.category}
                          </td>
                          <td className="px-4 py-3 align-top text-slate-800 max-w-xl">{row.message}</td>
                        </tr>
                        {open && hasMeta && (
                          <tr className="bg-slate-50/50">
                            <td colSpan={6} className="px-4 pb-4 pt-0">
                              <pre className="mt-2 p-3 rounded-lg bg-slate-900 text-emerald-400 text-xs overflow-x-auto font-mono">
                                {JSON.stringify(row.meta, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {data.pages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
            <span>
              Page {page} of {data.pages} ({data.total} total)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= data.pages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Monitoring;
